import axios from 'axios';

// Create axios instance with base URL
const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Attach the JWT to every request (same storage key as utils/api.js)
api.interceptors.request.use((config) => {
  try {
    const savedAuth = localStorage.getItem('glanalyzer_auth');
    if (savedAuth) {
      const authData = JSON.parse(savedAuth);
      if (authData.token) {
        config.headers.Authorization = `Bearer ${authData.token}`;
      }
    }
  } catch (e) {
    // If token parsing fails, send the request unauthenticated
  }
  return config;
});

// Trade API endpoints
const tradeAPI = {
  // Get all trades with optional filters
  getTrades: (filters = {}) => {
    const { startDate, endDate, symbol, period } = filters;
    let url = '/trades';
    const params = {};
    
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (symbol) params.symbol = symbol;
    if (period) params.period = period;
    
    return api.get(url, { params });
  },
  
  // Get a single trade by ID
  getTrade: (id) => api.get(`/trades/${id}`),
  
  // Get P/L summary grouped by time period
  getPLByPeriod: (period = 'month', startDate, endDate) => {
    const params = { period };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    return api.get('/trades/pl/period', { params });
  },
  
  // Get P/L summary grouped by symbol
  getPLBySymbol: (startDate, endDate) => {
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    return api.get('/trades/pl/symbol', { params });
  },
  
  // Get open positions grouped by symbol
  getOpenPositions: (filters = {}) => {
    const { account, accountId, live = true } = filters;
    const params = { live };
    
    if (account) params.account = account;
    if (accountId) params.accountId = accountId;
    
    return api.get('/trades/positions/open', { params });
  }
};

// Stock API endpoints
const stockAPI = {
  // Get all stocks
  getStocks: () => api.get('/stocks'),
  
  // Get a single stock by symbol
  getStock: (symbol) => api.get(`/stocks/${symbol}`),
  
  // Get performance summary for a stock
  getStockPerformance: (symbol, period = 'month') => {
    return api.get(`/stocks/${symbol}/performance`, {
      params: { period }
    });
  }
};

// Import API endpoints
const importAPI = {
  // Upload CSV file
  uploadCSV: (file, mappingId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mappingId', mappingId);
    
    return api.post('/imports/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // Get import history
  getImportHistory: () => api.get('/imports/history')
};

// Field Mapping API endpoints
const fieldMappingAPI = {
  // Get all field mappings
  getMappings: () => api.get('/field-mappings'),
  
  // Get a single mapping by ID
  getMapping: (id) => api.get(`/field-mappings/${id}`),
  
  // Create a new mapping
  createMapping: (mappingData) => api.post('/field-mappings', mappingData),
  
  // Update an existing mapping
  updateMapping: (id, mappingData) => api.put(`/field-mappings/${id}`, mappingData),
  
  // Delete a mapping
  deleteMapping: (id) => api.delete(`/field-mappings/${id}`),
  
  // Set a mapping as default
  setDefaultMapping: (id) => api.put(`/field-mappings/${id}/default`)
};

// Export all API services
export {
  tradeAPI,
  stockAPI,
  importAPI,
  fieldMappingAPI
};